import * as React from "react";
import { ForwardedRef, forwardRef, useEffect, useRef, useState } from "react";
import styled from "@mui/material/styles/styled";
import { GenericButton } from "../uiControllers/genericButton";
import { usePatient } from "../../state/patientContext";
import { DocQuestion } from "./DocQuestion";
import { CircularProgress } from "@mui/material";
import { useTranslate } from "../../hooks/useTranslate";

//region [[ Styles ]]

const StopCriteriaView = styled("div")({
  margin: "0",
});

const InfoButton = styled("div")({
  display: "flex",
  marginTop: "10px",
  marginBottom: "10px",
  justifyContent: "flex-end",
  "& .only-button": {
    minWidth: "120px",
  },
});

const Progress = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  position: "relative",
  "& .progress": {
    color: "white",
    "& svg": {
      margin: "0 !important",
    },
  },
  "& .countdown": {
    position: "absolute",
    fontSize: 12,
    lineHeight: 1,
  },
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface InfoElementProps {
  infoText: string;
  onContinue: () => void;
  continueLabel: string;
  onStop?: () => void;
  stopLabel?: string;
  noConfirm?: boolean;
  miniView?: boolean;
  answered?: boolean;
  infoId?: string;
  autoRedirect?: boolean;
}
const COUNTDOWN = 10;
//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const InfoElement = forwardRef(
  (
    { onContinue, autoRedirect, ...props }: InfoElementProps,
    ref: ForwardedRef<any>,
  ) => {
    const [count, setCount] = useState(COUNTDOWN);
    const { loading } = usePatient();
    const { translate } = useTranslate();
    const timer = useRef(null);
    useEffect(() => {
      if (autoRedirect) {
        timer.current = setInterval(() => {
          if (count === 0) {
            onContinue();
            return;
          }
          setCount((prevCount) => prevCount - 1);
        }, 1000);
      }
      return () => clearInterval(timer.current);
    }, [count, autoRedirect, onContinue]);

    const onStart = () => {
      onContinue();
    };

    return (
      <StopCriteriaView className={"stop-criteria-view"}>
        <DocQuestion text={props.infoText} miniView={props.miniView} />
        <InfoButton ref={props.answered || !props.noConfirm ? ref : null}>
          {!props.noConfirm && (
            <GenericButton
              className={props.onStop ? "primary-button" : "only-button"}
              onClick={onStart}
              label={props.continueLabel}
              disabled={props.answered || loading}
              additionalEvtData={{ infoId: props.infoId }}
              endIcon={
                autoRedirect && (
                  <Progress title={translate("REDIRECTING")}>
                    <CircularProgress
                      size={22}
                      value={count}
                      className={"progress"}
                    />
                    <div className={"countdown"}>{count}</div>
                  </Progress>
                )
              }
            />
          )}
          {props.onStop && (
            <GenericButton
              className={"secondary-button"}
              onClick={props.onStop}
              label={props.stopLabel}
              disabled={loading}
              additionalEvtData={{ infoId: props.infoId }}
            />
          )}
        </InfoButton>
      </StopCriteriaView>
    );
  },
);
